import { Countries, Industries } from '@platform/client';
import { OrganizationApplicationParameters } from '@platform/client/schemas';
import {
  MultiSelectCategory,
  MultiSelectCategoryDropdown,
  MultiSelectDropdown,
} from '@platform/components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { ApplicationPageProps } from '../../Application';
import { useApplicationLocalStorage } from '../../application-hook';
import { ApplicationBackground } from '../../ApplicationBackground';
import { ApplicationPageContent } from '../../components/ApplicationPageContent';
import BackgroundImage from '../../images/Leftsection13.svg';

type FormValues = {
  countries: string[];
  industries: string[];
};

export const OrgCountryOperationsSelection = (props: ApplicationPageProps) => {
  const { onBack, onContinue, progress, dataKey } = { ...props };

  const { updateApplicationData, getApplicationDataByKey } =
    useApplicationLocalStorage<OrganizationApplicationParameters>(dataKey);

  const countriesList = Countries.useGetCountries();

  const countryOptions =
    countriesList.isSuccess ?
      countriesList.data.map((country) => ({
        label: country.name,
        value: country.id,
      }))
    : [];

  const industriesList = Industries.useGetIndustries();
  const classificationOptions =
    industriesList.isSuccess ?
      Array.from(
        industriesList.data
          .reduce<Map<string, MultiSelectCategory>>((map, industry) => {
            if (!map.has(industry.classification)) {
              map.set(industry.classification, {
                name: industry.classification,
                options: [],
              });
            }
            // eslint-disable-next-line
            map.get(industry.classification)!.options.push(industry.name);
            return map;
          }, new Map())
          .values(),
      )
    : [];

  const applicationCountryIds = getApplicationDataByKey('countries') || [];
  const defaultCountries = countryOptions
    .filter((option) => applicationCountryIds.includes(option.value))
    .map((option) => option.label);

  const {
    handleSubmit,
    formState: { errors },
    register,
    watch,
    clearErrors,
    setValue,
  } = useForm<FormValues>({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      countries: defaultCountries,
      industries: getApplicationDataByKey('industries') || [],
    },
  });

  const submit: SubmitHandler<FormValues> = (data) => {
    const countryIds = data.countries.flatMap((country) => {
      const option = countryOptions.find((option) => option.label === country);
      return option ? [option.value] : [];
    });

    updateApplicationData({
      countries: countryIds,
      industries: data.industries,
    });
    onContinue();
  };

  const onCountriesSelectionChange = (value: string[]) => {
    setValue('countries', value);
    if (value.length > 0) {
      clearErrors('countries');
    }
  };

  const onIndustriesSelectionChange = (value: string[]) => {
    setValue('industries', value);
    if (value.length > 0) {
      clearErrors('industries');
    }
  };

  const handleBack = () => {
    const countryIds = watch('countries').flatMap((country) => {
      const option = countryOptions.find((option) => option.label === country);
      return option ? [option.value] : [];
    });
    updateApplicationData({
      countries: countryIds,
      industries: watch('industries'),
    });
    onBack();
  };

  return (
    <ApplicationBackground
      progress={progress}
      isOrganization={true}
      onBack={handleBack}
      onSubmit={handleSubmit(submit)}
      backgroundImage={BackgroundImage}
      isEditProfile={props.isEditProfile}
    >
      <ApplicationPageContent
        title='Where does your organization operate and what do you focus on?'
        description={
          'This helps us understand your geographic footprint and industry focus so we can connect you with the right support.'
        }
      >
        <MultiSelectDropdown
          register={() => {
            return register('countries', {
              required: 'Select the countries where your organization operates to continue',
            });
          }}
          selectedItems={watch('countries') || []}
          onSelectionChange={onCountriesSelectionChange}
          options={countryOptions}
          label='Select the countries where your organization operates'
          error={errors.countries?.message}
          filterable={true}
          placeholder='Select countries'
        />
        <MultiSelectCategoryDropdown
          selectedItems={watch('industries') || []}
          onSelectionChange={onIndustriesSelectionChange}
          categories={classificationOptions}
          selectionLimit={2}
          placeholder='Select two industries'
          label='Select up to 2 industries your organization operates in'
          error={errors.industries?.message}
          register={() => {
            return register('industries', {
              required: 'Select at least one industry to continue',
            });
          }}
        />
      </ApplicationPageContent>
    </ApplicationBackground>
  );
};
